import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STORAGE_KEY = "rp_consent_dismissed_until";

export default function ConsentBanner() {
  const { user } = useAuth();
  const [needs, setNeeds] = useState(false);
  const [version, setVersion] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    // Skip if dismissed recently (avoids re-showing on every page change after a "Remind me later")
    const dismissed = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (Date.now() < dismissed) return;
    api.get("/auth/consent")
      .then(({ data }) => {
        if (data?.needs_consent) {
          setVersion(data.current_version);
          setNeeds(true);
        }
      })
      .catch(() => {});
  }, [user]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post("/auth/consent", { version });
      localStorage.removeItem(STORAGE_KEY);
      setNeeds(false);
    } catch {}
    finally { setAccepting(false); }
  };

  const remindLater = () => {
    // Snooze for 24h
    localStorage.setItem(STORAGE_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setNeeds(false);
  };

  if (!needs) return null;

  return (
    <Dialog open={needs}>
      <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-w-lg" data-testid="consent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0055FF]" /> We've updated our policies
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Our <Link to="/privacy" target="_blank" className="text-[#3377FF] hover:underline">Privacy Policy</Link> and <Link to="/terms" target="_blank" className="text-[#3377FF] hover:underline">Terms & Conditions</Link> were updated on <span className="font-mono text-zinc-100">{version}</span>. Please review and confirm to continue using RankPilot AI.
          </p>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /> Clearer explanation of how AI processes your project data</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /> Updated rights for data export & deletion</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" /> Refined acceptable-use rules for AI-generated content</li>
          </ul>
          <div className="flex gap-2 pt-2">
            <Button onClick={accept} disabled={accepting} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="consent-accept">
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : "I agree & continue"}
            </Button>
            <Button onClick={remindLater} variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm h-11" data-testid="consent-later">
              Remind me later
            </Button>
          </div>
          <p className="text-[11px] text-zinc-600 text-center pt-2">
            By continuing, you confirm you've read and accept the latest Privacy Policy and Terms.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
